import { FiShield } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { RfxLead } from "newui/features/Rfx/types/rfx.types";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel";
import { ConflictPendingReviewNotice } from "../ConflictPendingReviewNotice";
import { PanelMemberBlockedReason } from "../../types/evaluation.type";

type RfxLeadInfo = RfxLead;

interface EvaluationAccessBoundaryProps {
	children: React.ReactNode;
	error: unknown;
	rfxLead?: RfxLeadInfo;
	scoringBlockedReason?: PanelMemberBlockedReason | null;
}

const isAxios403 = (error: unknown): boolean => {
	if (!error || typeof error !== "object") {
		return false;
	}
	const err = error as { response?: { status?: number }; status?: number };
	return err.response?.status === 403 || err.status === 403;
};

interface RevokedScreenProps {
	rfxLead?: RfxLeadInfo;
	scoringBlockedReason?: PanelMemberBlockedReason | undefined;
}

const RevokedScreen = ({ scoringBlockedReason, rfxLead }: RevokedScreenProps) => {
	const handleGoBack = (): void => {
		window.history.back();
	};

	return (
		<div
			data-testid="evaluation-access-revoked"
			className="flex min-h-[60vh] items-center justify-center p-6"
		>
			<div className="flex max-w-lg flex-col items-center rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
				<div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
					<FiShield
						className="h-6 w-6 text-amber-600"
						data-testid="evaluation-access-revoked-icon"
						aria-hidden="true"
					/>
				</div>
				<h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
				{!scoringBlockedReason &&
					<p className="mt-2 text-sm text-gray-500">
						Your access to this evaluation is denied. If you believe this
						is an error, please contact the RFx Lead.
					</p>}
				{rfxLead && (
					<div className="mt-4 w-full space-y-4">
						{scoringBlockedReason === "conflict_pending_review" && <ConflictPendingReviewNotice hideLeadInfo rfxLead={rfxLead} />}
						{scoringBlockedReason === "declined" && <p>You declined to participate in this evaluation.</p>}
						<RfxLeadInfoBox rfxLead={rfxLead} />
					</div>
				)}
				<Button
					btntype="secondary"
					onClick={handleGoBack}
					data-testid="evaluation-access-revoked-back"
					className="mt-6"
				>
					Go Back
				</Button>
			</div>
		</div>
	);
};

export const EvaluationAccessBoundary: React.FC<
	EvaluationAccessBoundaryProps
> = ({ error, rfxLead, scoringBlockedReason, children }) => {
	if (isAxios403(error) || scoringBlockedReason) {
		return <RevokedScreen scoringBlockedReason={scoringBlockedReason} rfxLead={rfxLead} />;
	}
	return <>{children}</>;
};
